import { useState } from "react";
import { useWallet } from "@/context/WalletContext";
import { multisendPreview, multisendExecute, fmtNum, shortAddr } from "@/lib/api";
import { PageHeader } from "@/dashboard/ui";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Check, AlertTriangle, Fuel, Users } from "lucide-react";

const SAMPLE = `0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b, 1000
0x2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c, 2500
0x3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d, 750.5`;

export default function Multisender() {
  const { address } = useWallet();
  const [raw, setRaw] = useState("");
  const [token, setToken] = useState("WLK");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);

  const parse = () => {
    const rows = [];
    raw.split("\n").forEach((line) => {
      const t = line.trim();
      if (!t) return;
      const [addr, amt] = t.split(/[,\s]+/);
      if (addr) rows.push({ address: addr, amount: Number(amt) || 0 });
    });
    return rows;
  };

  const runPreview = async () => {
    const rows = parse();
    if (rows.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    setLoading(true);
    try {
      const res = await multisendPreview({ wallet: address, token_symbol: token, rows });
      setPreview(res);
    } catch {
      toast.error("Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setState("pending");
    try {
      const rows = parse();
      const res = await multisendExecute({ wallet: address, token_symbol: token, rows });
      setResult(res);
      setState("success");
      toast.success("Batch sent", { description: `${res.recipients} recipients` });
    } catch {
      setState("idle");
      toast.error("Execution failed");
    }
  };

  const reset = () => {
    setConfirmOpen(false);
    setState("idle");
    setResult(null);
    setPreview(null);
    setRaw("");
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        eyebrow="batch transfer"
        title="Multisender"
        desc="Paste recipients as address, amount per line. Parse, preview, then send in one run."
      />

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="border border-welock-border bg-welock-surface p-6">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">Recipient list</span>
              <button data-testid="load-sample" onClick={() => setRaw(SAMPLE)} className="font-mono text-[11px] text-welock-accent hover:underline">
                Load sample
              </button>
            </div>
            <textarea
              data-testid="multisend-input"
              value={raw}
              onChange={(e) => { setRaw(e.target.value); setPreview(null); }}
              placeholder="0xabc..., 1000&#10;0xdef..., 2500"
              rows={12}
              className="w-full resize-none border border-welock-border bg-welock-bg p-4 font-mono text-xs text-welock-textPrimary outline-none focus:border-welock-accent/50"
            />
            <div className="mt-4 flex items-center justify-between">
              <div className="flex gap-2">
                {["WLK", "RBH", "USDC"].map((t) => (
                  <button
                    key={t}
                    data-testid={`ms-token-${t}`}
                    onClick={() => { setToken(t); setPreview(null); }}
                    className={`border px-3 py-1.5 font-mono text-xs ${token === t ? "border-welock-accent bg-welock-accent/10 text-welock-accent" : "border-welock-border text-welock-textSecondary"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <button
                data-testid="multisend-preview"
                onClick={runPreview}
                disabled={loading}
                className="flex items-center gap-2 border border-welock-border px-5 py-2.5 font-mono text-sm text-welock-textPrimary transition-colors hover:border-welock-accent/50 disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                Parse and preview
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="border border-welock-border bg-welock-surface p-6" data-testid="multisend-preview-panel">
            <span className="font-mono text-xs uppercase tracking-wider text-welock-textSecondary">Preview</span>
            {!preview ? (
              <div className="py-12 text-center font-mono text-xs text-welock-textMuted">Parse a list to preview</div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-px border border-welock-border bg-welock-border">
                  <Metric icon={Users} label="Recipients" value={fmtNum(preview.recipients)} />
                  <Metric label="Total" value={`${fmtNum(preview.total_amount)}`} accent />
                  <Metric label="Valid" value={fmtNum(preview.valid)} />
                  <Metric icon={AlertTriangle} label="Invalid" value={fmtNum(preview.invalid)} danger={preview.invalid > 0} />
                </div>
                <div className="flex items-center justify-between border border-welock-border bg-welock-bg px-4 py-3 font-mono text-xs">
                  <span className="flex items-center gap-1.5 text-welock-textMuted"><Fuel size={13} /> Est. gas</span>
                  <span className="text-welock-textPrimary">{preview.est_gas_rbh} RBH</span>
                </div>
                <button
                  data-testid="multisend-execute"
                  disabled={preview.valid === 0}
                  onClick={() => setConfirmOpen(true)}
                  className="w-full bg-welock-accent py-3 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-50"
                >
                  Send to {preview.valid} wallets
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => (!o && state !== "pending" ? reset() : null)}>
        <DialogContent data-testid="multisend-confirm" className="max-w-md border-welock-border bg-welock-surface p-0 text-welock-textPrimary [&>button]:text-welock-textMuted">
          {state !== "success" ? (
            <>
              <div className="border-b border-welock-border p-6">
                <span className="font-heading text-base font-semibold">Confirm batch transfer</span>
                <p className="mt-2 font-mono text-xs text-welock-textMuted">
                  Sending {fmtNum(preview?.total_amount)} {token} to {preview?.valid} recipients.
                </p>
              </div>
              <div className="flex gap-3 p-6">
                <button disabled={state === "pending"} onClick={() => setConfirmOpen(false)} className="flex-1 border border-welock-border py-3 font-mono text-sm text-welock-textSecondary disabled:opacity-50">Cancel</button>
                <button data-testid="multisend-confirm-send" disabled={state === "pending"} onClick={execute} className="flex flex-1 items-center justify-center gap-2 bg-welock-accent py-3 font-mono text-sm font-medium text-welock-bg disabled:opacity-70">
                  {state === "pending" ? <><Loader2 size={15} className="animate-spin" /> Sending</> : "Send batch"}
                </button>
              </div>
            </>
          ) : (
            <div className="p-8 text-center" data-testid="multisend-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center border border-welock-accent/40 bg-welock-accent/10">
                <Check size={26} className="text-welock-accent" />
              </div>
              <h3 className="mt-6 font-heading text-lg">Batch sent</h3>
              <p className="mt-2 font-mono text-xs text-welock-textMuted">{result.recipients} recipients, {fmtNum(result.total_amount)} {token}</p>
              <div className="mt-4 break-all border border-welock-border bg-welock-bg p-3 font-mono text-[11px] text-welock-textSecondary">
                tx {result.tx_hash.slice(0, 24)}...
              </div>
              <button data-testid="multisend-done" onClick={reset} className="mt-6 w-full bg-welock-accent py-3 font-mono text-sm font-medium text-welock-bg">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, danger }) {
  return (
    <div className="bg-welock-surface p-4">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-welock-textMuted">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className={`mt-1.5 font-heading text-lg ${danger ? "text-welock-danger" : accent ? "text-welock-accent" : "text-welock-textPrimary"}`}>{value}</div>
    </div>
  );
}
